// Audience measurement.
//
// Umami, cookieless: the tag is in the shell (public/index.html) and this is the
// only module that knows it exists. Nothing here writes to the device — no
// cookie, no localStorage, no sessionStorage — which is the whole reason the
// site carries no consent banner. Whatever is added below must keep that true;
// the moment analytics stores something on a reader's machine, ePrivacy Art.
// 5(3) applies and the banner comes with it. See doc/compliance/.
//
// Two rules the props obey, and they are not stylistic:
//   1. Content identifiers only — a city id, a country slug, a variable key.
//      Never anything derived from the reader, and above all never the text
//      typed into the search box. Free text a visitor wrote is a different risk
//      class than "somebody opened Lisbon".
//   2. Nothing that could tie two visits together. The visitor hash rotates
//      daily by design; a prop that survived that would quietly undo it.
//
// The tag is loaded with data-auto-track="false" because the address bar is
// rewritten constantly by url-sync — scrubbing the year would otherwise be read
// as dozens of page views. So page views are sent from here, deliberately.

type Props = Record<string, string | number | boolean>;

interface Umami {
  track: ((name: string, props?: Props) => void) & (() => void);
}

declare global {
  interface Window {
    umami?: Umami;
  }
}

// app.js and the tracker are both deferred, and the tracker is the later tag, so
// the first page view can be raised before window.umami exists. It is also
// permanently absent for every reader running a blocker, which is a real share
// of the traffic. Both cases land here: buffer a little, drain when it shows up,
// and never let either turn into an error the reader can see.
const pending: Array<{ name: string | null; props?: Props }> = [];
const PENDING_MAX = 20;

function drain() {
  const umami = window.umami;
  if (!umami || !pending.length) return;
  for (const ev of pending.splice(0, pending.length)) {
    try {
      if (ev.name == null) umami.track();
      else umami.track(ev.name, ev.props);
    } catch {
      /* analytics must never break the map */
    }
  }
}

function send(name: string | null, props?: Props) {
  if (!window.umami) {
    if (pending.length < PENDING_MAX) pending.push({ name, props });
    return;
  }
  drain();
  try {
    if (name == null) window.umami.track();
    else window.umami.track(name, props);
  } catch {
    /* as above — a failed beacon is not worth a broken atlas */
  }
}

// Whether the app is currently replaying a route rather than answering a reader.
//
// Boot and Back/Forward drive the whole view through the same mutators a click
// goes through — openCity, showFrame, applyVariable, setView — so without this
// every deep link would report a burst of interactions nobody performed. Starts
// true because that is exactly what a page load is; applyRouteFromUrl clears it
// when the route has landed.
//
// url-sync's own `applyingRoute` flag looks like the same fact and is not: it is
// cleared early, mid-route, so openCity's persist() can write the city URL (see
// url-sync.ts). Borrowing it here would let a deep-linked city count as opened.
let replaying = true;

export function setReplaying(on: boolean) {
  replaying = on;
}

// One page view, sent by hand: once when a route has landed, and on each view
// change after that — landing, map, a city briefing are what a reader would call
// pages here. Suppressed while replaying for the same reason events are, so a
// deep link reports one page view rather than one per view the replay passes
// through on its way to the right one.
export function trackPageview() {
  if (replaying) return;
  send(null);
}

// The address bar is written on a 350ms trailing debounce (syncUrl in
// url-sync.ts), so a view change reaches this module BEFORE the URL it produced
// exists. Sending immediately stamps the page view with the address the reader
// just left — every city opened in-app would be filed under "/" rather than
// under the city, and only direct loads and Back/Forward would land correctly.
//
// So an in-app page view waits out that debounce. Trailing, and shared with
// itself, so landing → map → city in quick succession is one page view of where
// the reader ended up rather than three of where they passed through; the
// view_change events still record the path they took.
//
// The delay is stated here rather than imported: url-sync already imports this
// module, and reaching back for the constant would close a cycle for a number.
const URL_SETTLE_MS = 420;
let pageviewTimer: ReturnType<typeof setTimeout> | null = null;

export function trackPageviewDeferred() {
  if (pageviewTimer) clearTimeout(pageviewTimer);
  pageviewTimer = setTimeout(() => {
    pageviewTimer = null;
    trackPageview();
  }, URL_SETTLE_MS);
}

// An interaction the reader chose.
export function track(name: string, props?: Props) {
  if (replaying) return;
  send(name, props);
}

// For controls that fire continuously. The ruler is a native range input, so a
// single drag across the year raises up to 36 date changes; what we want is the
// date the reader landed on. Keyed by event name so two debounced controls do
// not eat each other's timers.
const timers = new Map<string, ReturnType<typeof setTimeout>>();

export function trackDebounced(name: string, props: Props | undefined, ms: number) {
  const prev = timers.get(name);
  if (prev) clearTimeout(prev);
  timers.set(
    name,
    setTimeout(() => {
      timers.delete(name);
      track(name, props);
    }, ms),
  );
}

window.addEventListener('load', drain);
